import { useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { Search, Home, TrendingUp, Newspaper, ArrowLeft } from 'lucide-react';
import { NewsLayout } from '@/layouts/NewsLayout';
import { SEOHead } from '@/components/SEOHead';
import { usePopularArticles } from '@/hooks/useArticles';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const { data: popular = [] } = usePopularArticles(5);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/news/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <NewsLayout>
      <SEOHead
        title="Stran ni najdena (404)"
        description="Iskana stran ne obstaja. Preverite URL naslov ali poiščite vsebino."
        noindex
      />

      <div className="container py-16 md:py-24">
        <div className="max-w-2xl mx-auto text-center">
          {/* Big 404 */}
          <p className="text-8xl md:text-9xl font-black text-primary/20 leading-none select-none">
            404
          </p>

          <h1 className="font-serif text-2xl md:text-3xl font-bold mt-4 mb-3">
            Ups! Te strani ne najdemo
          </h1>
          <p className="text-muted-foreground mb-8 text-lg">
            Stran <code className="bg-muted px-2 py-0.5 rounded text-sm">{location.pathname}</code> ne obstaja ali je bila premaknjena.
          </p>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex gap-2 max-w-md mx-auto mb-10">
            <Input
              type="search"
              placeholder="Iskanje člankov..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1"
            />
            <Button type="submit" size="icon">
              <Search className="w-4 h-4" />
            </Button>
          </form>

          {/* Quick links */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            <Button variant="outline" asChild>
              <Link to="/"><Home className="w-4 h-4 mr-2" />Domov</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/news/latest"><Newspaper className="w-4 h-4 mr-2" />Najnovejše</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/news/popular"><TrendingUp className="w-4 h-4 mr-2" />Popularno</Link>
            </Button>
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />Nazaj
            </Button>
          </div>

          {/* Popular articles */}
          {popular.length > 0 && (
            <div className="text-left border-t border-border pt-8">
              <h2 className="font-serif text-lg font-bold mb-4">Morda vas zanima</h2>
              <ul className="space-y-3">
                {popular.map((article) => (
                  <li key={article.id}>
                    <Link
                      to={`/article/${article.slug}`}
                      className="group flex items-start gap-3"
                    >
                      {article.featured_image && (
                        <img
                          src={article.featured_image}
                          alt={article.title}
                          className="w-16 h-12 rounded object-cover shrink-0"
                          loading="lazy"
                          width={64}
                          height={48}
                        />
                      )}
                      <span className="text-sm font-medium group-hover:text-primary transition-colors line-clamp-2">
                        {article.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </NewsLayout>
  );
};

export default NotFound;
